/**
 * Entry point.
 *
 * Deliberately thin: find the canvas, wire up the PWA bits, construct the App, and get out
 * of the way. Anything interesting belongs in `ui/app.ts`.
 *
 * The one job that genuinely lives here is **failing visibly**. A blank canvas is the worst
 * possible outcome for a game people are trying to play on a television, so a boot failure
 * paints a readable message into the DOM rather than only into the console.
 */

import './styles.css';
import { App } from './ui/app';
import {
  applyUpdate, capturePwaInstallPrompt, keepScreenAwake, registerServiceWorker,
  requestFullscreen,
} from './pwa';
import { GAME_TITLE, GAME_VERSION } from './game/constants';

function fatal(message: string, detail?: unknown): void {
  // eslint-disable-next-line no-console -- a boot failure must be loud
  console.error('[nova-charter] boot failed:', message, detail);

  const root = document.getElementById('ui-root') ?? document.body;
  const box = document.createElement('div');
  box.className = 'boot-error';
  box.setAttribute('role', 'alert');
  box.innerHTML = `
    <h1>${GAME_TITLE} could not start</h1>
    <p>${escapeHtml(message)}</p>
    <p class="boot-error__hint">
      This game needs a modern browser with Canvas support. Try the latest Chrome, Edge,
      Firefox or Safari.
    </p>
  `;
  root.appendChild(box);
  document.body.setAttribute('data-booted', 'error');
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] ?? ch
  ));
}

function boot(): void {
  const canvas = document.getElementById('stage');
  if (!(canvas instanceof HTMLCanvasElement)) {
    fatal('The render surface is missing from the page.');
    return;
  }

  let app: App;
  try {
    app = new App(canvas);
  } catch (error) {
    fatal(error instanceof Error ? error.message : 'Unknown start-up error.', error);
    return;
  }

  // Stash the install prompt before anything can consume it.
  capturePwaInstallPrompt();

  try {
    app.start();
  } catch (error) {
    fatal(error instanceof Error ? error.message : 'The game loop failed to start.', error);
    return;
  }

  // A local four-player match runs about an hour with long stretches of no input on the
  // machine's own keyboard, so the display would otherwise sleep mid-game.
  void keepScreenAwake();

  // Full-screen is a request, not a demand: browsers only grant it from a user gesture, so
  // hook the first one and never nag again.
  const goFullscreen = () => {
    void requestFullscreen(document.documentElement);
    window.removeEventListener('pointerdown', goFullscreen);
    window.removeEventListener('keydown', goFullscreen);
  };
  window.addEventListener('pointerdown', goFullscreen, { once: true });
  window.addEventListener('keydown', goFullscreen, { once: true });

  void registerServiceWorker({
    onUpdateReady: () => {
      // Never yank the page out from under a match in progress. Take the update on the
      // next load instead, unless the player is still sitting on the title screen.
      // eslint-disable-next-line no-console -- useful, low-frequency
      console.info('[nova-charter] a new version is ready; it will load next time.');
      if (document.visibilityState === 'hidden') applyUpdate();
    },
  });

  // Expose a handle for debugging from the console. Always on in development, and
  // opt-in via `?debug=1` in production so a deployed build can still be inspected —
  // and so the browser smoke test can drive the real bundle rather than a dev build.
  if (import.meta.env.DEV || new URLSearchParams(location.search).get('debug') === '1') {
    Reflect.set(window, 'novaCharter', { app, version: GAME_VERSION });
  }

  window.addEventListener('pagehide', () => app.dispose(), { once: true });
}

// `defer`-like semantics without relying on script placement.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
